import React, { useState, useRef, useContext } from "react";
import { addExpenseLine, updateExpenseLine, uploadReceipt, getMyImages } from "../api";
import { CATEGORIES, MILEAGE_RATE } from "../authConfig";
import { ToastContext } from "../App";

// editLine: existing line object to edit (optional — omit for add mode)
export default function AddExpenseModal({ user, reportId, onClose, onAdded, editLine }) {
  const showToast = useContext(ToastContext);
  const fileInputRef = useRef(null);

  const today = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState({
    expenseDate: editLine?.ExpenseDate?.slice(0, 10) || today,
    category: editLine?.Category || "",
    amount: editLine?.Amount != null ? String(editLine.Amount) : "",
    miles: editLine?.Miles != null ? String(editLine.Miles) : "",
    description: editLine?.Description || "",
  });
  const [receiptFile, setReceiptFile]       = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(editLine?.ReceiptUrl || null);
  const [saving, setSaving]                 = useState(false);

  // Receipt picker state
  const [showReceiptPicker, setShowReceiptPicker] = useState(false);
  const [savedImages, setSavedImages]             = useState([]);
  const [loadingSaved, setLoadingSaved]           = useState(false);
  const [showSavedPicker, setShowSavedPicker]     = useState(false);

  const isEdit    = Boolean(editLine);
  const isMileage = form.category === "Mileage";

  // Detect whether the current receipt is a PDF (new file or existing URL)
  const isPdf = receiptFile
    ? receiptFile.type === "application/pdf"
    : receiptPreview
      ? /\.pdf(\?|$)/i.test(receiptPreview)
      : false;
  const milesNum  = parseFloat(form.miles) || 0;
  const calcAmount = isMileage ? (milesNum * MILEAGE_RATE).toFixed(2) : form.amount;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setReceiptFile(file);
    setReceiptPreview(URL.createObjectURL(file));
    setShowReceiptPicker(false);
  };

  const handlePickSaved = (img) => {
    // Use the SAS URL directly as the receipt URL (already uploaded to blob storage)
    setReceiptFile(null);
    setReceiptPreview(img.url);
    // Store the raw SAS URL so we can pass it straight through without re-uploading
    setReceiptUrlOverride(img.url);
    setShowSavedPicker(false);
    setShowReceiptPicker(false);
  };

  const [receiptUrlOverride, setReceiptUrlOverride] = useState(null);

  const openSavedPicker = () => {
    setLoadingSaved(true);
    getMyImages()
      .then((data) => setSavedImages(data.images || []))
      .catch(() => showToast("Failed to load saved images", "error"))
      .finally(() => setLoadingSaved(false));
    setShowSavedPicker(true);
    setShowReceiptPicker(false);
  };

  const handleSave = async () => {
    if (!form.category) { showToast("Please select a category", "error"); return; }
    if (!form.expenseDate) { showToast("Please select a date", "error"); return; }
    if (isMileage && !form.miles) { showToast("Please enter miles", "error"); return; }
    if (!isMileage && !form.amount) { showToast("Please enter an amount", "error"); return; }

    try {
      setSaving(true);

      let receiptUrl = isEdit ? (editLine.ReceiptUrl || null) : null;
      if (receiptUrlOverride) {
        receiptUrl = receiptUrlOverride;
      } else if (receiptFile) {
        const { url } = await uploadReceipt(receiptFile);
        receiptUrl = url;
      }

      const payload = {
        expenseDate: form.expenseDate,
        category: form.category,
        amount: isMileage ? parseFloat(calcAmount) : parseFloat(form.amount),
        miles: isMileage ? milesNum : null,
        description: form.description,
        receiptUrl,
      };

      if (isEdit) {
        const result = await updateExpenseLine(editLine.ID, payload);
        showToast("Expense updated!", "success");
        onAdded(result.line);
      } else {
        const result = await addExpenseLine({ reportId: reportId || null, ...payload });
        showToast("Expense added!", "success");
        onAdded(result.line);
      }
    } catch (e) {
      showToast(e.message || "Failed to save expense", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="modal">
          <div className="modal-handle" />
          <div className="modal-title">{isEdit ? "Edit Expense" : "Add Expense"}</div>

          {/* Date + Category */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date</label>
              <input
                type="date"
                className="form-control"
                value={form.expenseDate}
                onChange={set("expenseDate")}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-control" value={form.category} onChange={set("category")}>
                <option value="">Select…</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Amount or Mileage */}
          {isMileage ? (
            <div className="form-group">
              <label className="form-label">Miles Driven</label>
              <input
                type="number"
                className="form-control"
                placeholder="0"
                min="0"
                step="0.1"
                value={form.miles}
                onChange={set("miles")}
                inputMode="decimal"
              />
              {milesNum > 0 && (
                <div className="mileage-calc">
                  🚗 {milesNum} miles × ${MILEAGE_RATE}/mi ={" "}
                  <strong>${calcAmount}</strong>
                </div>
              )}
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Amount ($)</label>
              <input
                type="number"
                className="form-control"
                placeholder="0.00"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={set("amount")}
                inputMode="decimal"
              />
            </div>
          )}

          {/* Receipt */}
          <div className="form-group">
            <label className="form-label">Receipt</label>
            <button
              type="button"
              className={`receipt-btn${receiptPreview ? " has-receipt" : ""}`}
              onClick={() => setShowReceiptPicker(true)}
            >
              {receiptPreview ? "📎 Receipt attached — tap to change" : "📎 Add Receipt"}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*,.pdf"
              style={{ display: "none" }} onChange={handleFileInput} />
            {receiptPreview && (
              isPdf ? (
                <div style={{
                  marginTop: 10, padding: "12px 16px", borderRadius: 10,
                  background: "var(--gray-1)", border: "1.5px solid var(--gray-2)",
                  display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "var(--gray-5, #333)",
                }}>
                  <span style={{ fontSize: 24 }}>📄</span>
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {receiptFile ? receiptFile.name : "PDF receipt attached"}
                  </span>
                </div>
              ) : (
                <img src={receiptPreview} alt="Receipt preview" className="receipt-preview" />
              )
            )}
          </div>

          {/* Optional description */}
          <div className="form-group">
            <label className="form-label">
              Note <span style={{ fontWeight: 400, color: "var(--gray-4)" }}>(optional)</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. Client dinner, lost receipt, shared with team…"
              value={form.description}
              onChange={set("description")}
            />
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
              style={{ flex: 2 }}
            >
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Save Expense"}
            </button>
          </div>
        </div>
      </div>

      {/* Receipt source picker */}
      {showReceiptPicker && (
        <div className="modal-overlay" onClick={() => setShowReceiptPicker(false)}>
          <div className="modal" style={{ maxWidth: 340 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-handle" />
            <div className="modal-title" style={{ marginBottom: 16 }}>Add Receipt</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button className="btn btn-ghost" style={{ justifyContent: "flex-start", gap: 12, fontSize: 15, padding: "14px 16px" }}
                onClick={() => fileInputRef.current?.click()}>
                <span style={{ fontSize: 22 }}>📷</span> Take photo, choose image or PDF
              </button>
              <button className="btn btn-ghost" style={{ justifyContent: "flex-start", gap: 12, fontSize: 15, padding: "14px 16px" }}
                onClick={openSavedPicker}>
                <span style={{ fontSize: 22 }}>⭐</span> My Saved Images
              </button>
              <button className="btn btn-ghost" style={{ marginTop: 4 }} onClick={() => setShowReceiptPicker(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* My Saved Images picker */}
      {showSavedPicker && (
        <div className="modal-overlay" onClick={() => setShowSavedPicker(false)}>
          <div className="modal" style={{ maxHeight: "80vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-handle" />
            <div className="modal-title" style={{ marginBottom: 12 }}>My Saved Images</div>
            {loadingSaved ? (
              <div className="loader"><div className="spinner" /></div>
            ) : savedImages.length === 0 ? (
              <div style={{ textAlign: "center", color: "var(--gray-4)", padding: "24px 0", fontSize: 14 }}>
                No saved images yet.<br />Add some from the My Images tab.
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {savedImages.map((img) => (
                  <div
                    key={img.name}
                    onClick={() => handlePickSaved(img)}
                    style={{ cursor: "pointer", borderRadius: 10, overflow: "hidden", border: "2px solid var(--gray-2)" }}
                  >
                    <img
                      src={img.url}
                      alt={img.label || "Saved image"}
                      style={{
                        width: "100%", aspectRatio: "4/3", objectFit: "cover", display: "block",
                      }}
                    />
                    <div style={{
                      fontSize: 12, color: "var(--gray-5, #222)", padding: "5px 8px",
                      background: "var(--gray-1)", textAlign: "center",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      fontWeight: 500,
                    }}>
                      {img.label || img.name}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button className="btn btn-ghost" style={{ marginTop: 14, width: "100%" }}
              onClick={() => setShowSavedPicker(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
